/**
 * accountTypeUtils.js
 *
 * Single source of truth for:
 *   1. normalising the backend `account.account_type` value into a lookup key
 *   2. resolving the account-policy badge presentation for that type
 *
 * WHY THIS EXISTS
 * ---------------
 * The production `account.account_type` enum has exactly two values:
 *   - `brand`       -> unified / single-brand account
 *   - `multivendor` -> multi-vendor marketplace account
 *
 * The legacy spellings (`unified brand`, `unified_brand`, `single_seller`, ...)
 * are kept as aliases so older records and seed data keep rendering correctly.
 *
 * Normalisation used to be re-implemented inline in every component that
 * needed it. `AccountPolicyBadge` only knew the legacy keys, so the real
 * production value `brand` fell through to a "Multi-Vendor" label on EVERY
 * page that renders the badge. Centralising the key space fixes that class of
 * bug for good: one normaliser, one config table, one fallback.
 */

/** Key of the neutral fallback entry (unknown / missing account type). */
export const ACCOUNT_TYPE_UNKNOWN = 'unknown';

/**
 * Normalise a raw `account_type` into its canonical lookup key.
 *
 *   'Unified Brand' | 'unified_brand' | 'unified-brand' -> 'unifiedbrand'
 *   'brand'                                           -> 'brand'
 *   'Multi-Vendor'                                    -> 'multivendor'
 *
 * @param {string|undefined|null} rawType raw `account_type` from the API
 * @returns {string} normalised key (empty string when no type is provided)
 */
export const normalizeAccountType = (rawType) => {
  if (typeof rawType !== 'string') return '';
  return rawType.trim().toLowerCase().replace(/[\s_-]+/g, '');
};

/** Canonical account-type key for the marketplace (multi-vendor) model. */
export const ACCOUNT_TYPE_MULTIVENDOR = 'multivendor';

/** Canonical account-type key for the single-brand / unified model. */
export const ACCOUNT_TYPE_BRAND = 'brand';

/**
 * Collapse an accepted spelling of `account_type` onto the two production
 * values, or `''` when the value is missing / unrecognised.
 *
 *   'brand' | 'Unified Brand' | 'unified_brand' -> 'brand'
 *   'multivendor' | 'Multi-Vendor' | 'multi_vendor' -> 'multivendor'
 *
 * Essential-seller and single-seller are NOT collapsed here - use
 * `isBranchWordingAccountType` when the question is "branch or seller wording".
 *
 * @param {string|undefined|null} rawType raw `account_type` from the API
 * @returns {'brand'|'multivendor'|''}
 */
export const canonicalAccountType = (rawType) => {
  const key = normalizeAccountType(rawType);
  if (key === 'multivendor') return ACCOUNT_TYPE_MULTIVENDOR;
  if (key === 'brand' || key === 'unifiedbrand') return ACCOUNT_TYPE_BRAND;
  return '';
};

/**
 * Is this a BRAND account (the single-brand / unified model)?
 *
 * `brand` is the production enum value; `unifiedbrand` is its legacy alias.
 * Strictly those two - essential-seller and single-seller keep their own
 * policy identity (see `isBranchWordingAccountType`).
 *
 * @param {string|undefined|null} rawType the raw `account_type`
 * @returns {boolean}
 */
export const isBrandAccountType = (rawType) => {
  const key = normalizeAccountType(rawType);
  return key === 'brand' || key === 'unifiedbrand';
};

/**
 * Is this an account that speaks about BRANCHES rather than SELLERS?
 *
 * This is the wording decision of the admin shell: the single-brand / unified
 * model (brand, its legacy alias, essential-seller and single-seller) speaks
 * about branches; a marketplace speaks about sellers. It is deliberately wider
 * than `isBrandAccountType`, which stays strict.
 *
 * @param {string|undefined|null} rawType the raw `account_type`
 * @returns {boolean}
 */
export const isBranchWordingAccountType = (rawType) => {
  const key = normalizeAccountType(rawType);
  return (
    key === 'brand' ||
    key === 'unifiedbrand' ||
    key === 'essentialseller' ||
    key === 'singleseller'
  );
};

/**
 * Is this a MULTIVENDOR (marketplace) account?
 *
 * @param {string|undefined|null} rawType the raw `account_type`
 * @returns {boolean}
 */
export const isMultivendorAccountType = (rawType) => {
  const key = normalizeAccountType(rawType);
  return key === 'multivendor';
};

/**
 * Badge presentation per normalised account type.
 *
 * `labelKey` / `tooltipKey` live in the `admin` translation namespace.
 * `bgColor` / `textColor` are applied inline so each account type keeps its
 * own visual identity.
 *
 * NOTE: `brand` is the PRODUCTION enum value for the unified / single-brand
 * concept, so it deliberately renders the exact same label, tooltip and
 * colours as the legacy `unifiedbrand` alias.
 */
export const ACCOUNT_POLICY_BADGE_CONFIG = {
  // Production value for unified / single-brand accounts.
  brand: {
    labelKey: 'accountPolicyBadge.unifiedBrand',
    tooltipKey: 'accountPolicyBadge.tooltip.unifiedBrand',
    bgColor: '#3b82f6',
    textColor: '#ffffff',
  },
  // Legacy alias - kept for forward/backward compatibility.
  unifiedbrand: {
    labelKey: 'accountPolicyBadge.unifiedBrand',
    tooltipKey: 'accountPolicyBadge.tooltip.unifiedBrand',
    bgColor: '#3b82f6',
    textColor: '#ffffff',
  },
  essentialseller: {
    labelKey: 'accountPolicyBadge.essentialSeller',
    tooltipKey: 'accountPolicyBadge.tooltip.essentialSeller',
    bgColor: '#10b981',
    textColor: '#ffffff',
  },
  singleseller: {
    labelKey: 'accountPolicyBadge.singleSeller',
    tooltipKey: 'accountPolicyBadge.tooltip.singleSeller',
    bgColor: '#10b981',
    textColor: '#ffffff',
  },
  multivendor: {
    labelKey: 'accountPolicyBadge.multivendor',
    tooltipKey: 'accountPolicyBadge.tooltip.multivendor',
    bgColor: '#6b7280',
    textColor: '#ffffff',
  },
  /**
   * Neutral fallback for an account type we do not recognise.
   *
   * It is deliberately NEUTRAL (light slate chip + muted label) and it never
   * claims a policy: an unknown type must not be rendered as "Multi-Vendor",
   * because that silently mislabels single-brand accounts. Rendering a neutral
   * badge (rather than nothing) keeps the header layout stable and still
   * tells the user "we could not determine this account's policy".
   */
  [ACCOUNT_TYPE_UNKNOWN]: {
    labelKey: 'accountPolicyBadge.unknown',
    tooltipKey: 'accountPolicyBadge.tooltip.unknown',
    bgColor: '#e2e8f0',
    textColor: '#334155',
  },
};

/**
 * Resolve the badge configuration for a raw `account_type`.
 *
 * @param {string|undefined|null} rawType raw `account_type` from the API
 * @returns {{labelKey:string, tooltipKey:string, bgColor:string, textColor:string}}
 *          the matching config, or the neutral fallback config.
 */
export const resolveAccountPolicyBadgeConfig = (rawType) => {
  const key = normalizeAccountType(rawType);
  return (
    ACCOUNT_POLICY_BADGE_CONFIG[key] ||
    ACCOUNT_POLICY_BADGE_CONFIG[ACCOUNT_TYPE_UNKNOWN]
  );
};

export default ACCOUNT_POLICY_BADGE_CONFIG;
